import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_URL } from "../config";

interface Trip {
  id: number;
  title: string;
}

interface BookingTour {
  id: number;
  date_booking: string;
  email: string;
  firstname: string;
  lastname: string;
  phone: string;
  trip_id: number;
  status: string;
  trip?: Trip;
}

interface IconProps {
  size?: number;
  color?: string;
}

const iconBase = (size: number) => ({
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none" as const,
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
});

const SearchIcon: React.FC<IconProps> = ({ size = 16, color = "currentColor" }) => (
  <svg {...iconBase(size)} stroke={color}>
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const XIcon: React.FC<IconProps> = ({ size = 16, color = "currentColor" }) => (
  <svg {...iconBase(size)} stroke={color}>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const EyeIcon: React.FC<IconProps> = ({ size = 16, color = "currentColor" }) => (
  <svg {...iconBase(size)} stroke={color}>
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const TrashIcon: React.FC<IconProps> = ({ size = 15, color = "currentColor" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 16 16" fill={color}>
    <path d="M 6.496094 1 C 5.675781 1 5 1.675781 5 2.496094 L 5 3 L 2 3 L 2 4 L 3 4 L 3 12.5 C 3 13.328125 3.671875 14 4.5 14 L 10.5 14 C 11.328125 14 12 13.328125 12 12.5 L 12 4 L 13 4 L 13 3 L 10 3 L 10 2.496094 C 10 1.675781 9.324219 1 8.503906 1 Z M 6.496094 2 L 8.503906 2 C 8.785156 2 9 2.214844 9 2.496094 L 9 3 L 6 3 L 6 2.496094 C 6 2.214844 6.214844 2 6.496094 2 Z M 5 5 L 6 5 L 6 12 L 5 12 Z M 7 5 L 8 5 L 8 12 L 7 12 Z M 9 5 L 10 5 L 10 12 L 9 12 Z" />
  </svg>
);

const ChevronLeftIcon: React.FC<IconProps> = ({ size = 16, color = "currentColor" }) => (
  <svg {...iconBase(size)} stroke={color}>
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRightIcon: React.FC<IconProps> = ({ size = 16, color = "currentColor" }) => (
  <svg {...iconBase(size)} stroke={color}>
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const colors = {
  primary: "#294b5a",
  primaryHover: "#1a3641",
  text: "#1e293b",
  textMuted: "#5f6b76",
  textFaint: "#94a3b8",
  border: "#dbe0e4",
  borderLight: "#e5e7eb",
  bgSubtle: "#f8fafc",
  danger: "#ef4444",
};

const cardStyle: React.CSSProperties = {
  background: "#fff",
  border: `0.5px solid ${colors.borderLight}`,
  borderRadius: 12,
};

const buttonBase: React.CSSProperties = {
  padding: "8px 14px",
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  border: "1px solid transparent",
  transition: "all .15s ease",
};

const ListBookingTours = () => {
  const [bookingTours, setBookingTours] = useState<BookingTour[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  const fetchBookingTours = (page = 1) => {
    axios
      .get(`${API_URL}/booking-trips?page=${page}`)
      .then((res) => {
        setBookingTours(res.data.data);
        setCurrentPage(res.data.current_page || 1);
        setLastPage(res.data.last_page || 1);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  useEffect(() => {
    fetchBookingTours();
  }, []);

  const filteredBookingTours = useMemo(() => {
    if (!searchTerm) return bookingTours;

    const lowercasedSearchTerm = searchTerm.toLowerCase();

    return bookingTours.filter(
      (booking) =>
        booking.firstname.toLowerCase().includes(lowercasedSearchTerm) ||
        booking.lastname.toLowerCase().includes(lowercasedSearchTerm) ||
        booking.email.toLowerCase().includes(lowercasedSearchTerm) ||
        booking.trip?.title?.toLowerCase().includes(lowercasedSearchTerm) ||
        booking.status.toLowerCase().includes(lowercasedSearchTerm)
    );
  }, [bookingTours, searchTerm]);

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    if (dateString.includes(" ")) return dateString.split(" ")[0];
    if (dateString.includes("T")) return dateString.split("T")[0];
    return dateString;
  };

  const getStatusBadgeStyle = (status: string): React.CSSProperties => {
    const s = status.toLowerCase();
    if (s === "confirmed") return { background: "#dcfce7", color: "#15803d" };
    if (s === "pending") return { background: "#fef9c3", color: "#a16207" };
    if (s === "cancelled") return { background: "#fee2e2", color: "#b91c1c" };
    if (s === "completed") return { background: "#dbeafe", color: "#1d4ed8" };
    return { background: "#f1f5f9", color: "#475569" };
  };

  const formatStatus = (status: string) => {
    const statusTexts: Record<string, string> = {
      pending: "Pending",
      confirmed: "Confirmed",
      cancelled: "Cancelled",
      completed: "Completed",
    };
    return statusTexts[status] || status;
  };

  const handleDeleteClick = (rowIndex: number) => {
    setSelectedRow(rowIndex);
    setShowModal(true);
  };

  const handleCancel = () => {
    setShowModal(false);
    setSelectedRow(null);
  };

  const handleConfirmDelete = async () => {
    if (selectedRow === null) return;
    try {
      const bookingTourToDelete = filteredBookingTours[selectedRow];
      await axios.delete(`${API_URL}/booking-trip/${bookingTourToDelete.id}`);

      setBookingTours((prevBookingTours) =>
        prevBookingTours.filter((bookingTour) => bookingTour.id !== bookingTourToDelete.id)
      );
      fetchBookingTours();
      toast.success("Tour booking deleted successfully");
    } catch (error: any) {
      console.error("Error deleting tour booking:", error.response || error.message);
      toast.error("Error deleting tour booking");
    } finally {
      setShowModal(false);
      setSelectedRow(null);
    }
  };

  const handleEditClick = (id: number) => {
    navigate(`/admin/details-booking-trip/${id}`);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  return (
    <>
      {/* Grille : Left (20%), Center (60%), Right (20%) */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "20% 60% 20%",
          gap: "16px",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {/* Colonne Gauche (20%) - Filtres Rapides / Infos de Contexte */}
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div style={{ ...cardStyle, padding: 16 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, margin: "0 0 12px 0", color: colors.text }}>
              Quick Info
            </h3>
            <p style={{ fontSize: 12, color: colors.textMuted, margin: 0, lineHeight: 1.5 }}>
              Use the center section to search and manage tour reservations.
            </p>
          </div>
        </div>

        {/* Colonne Centre (60%) - Contenu Principal */}
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {/* Section Header & Recherche */}
          <div style={{ ...cardStyle, padding: 16 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 12,
              }}
            >
              <div>
                <h1 style={{ fontSize: 18, fontWeight: 600, margin: 0, color: colors.text }}>
                  Manage Booking Tours
                </h1>
                <span style={{ fontSize: 12, color: colors.textMuted }}>
                  {bookingTours.length} total bookings
                  {searchTerm && ` • ${filteredBookingTours.length} filtered`}
                </span>
              </div>
            </div>

            {/* Barre de recherche */}
            <div style={{ position: "relative", width: "100%" }}>
              <span
                style={{
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  display: "flex",
                  color: colors.textFaint,
                }}
              >
                <SearchIcon size={16} />
              </span>
              <input
                type="text"
                placeholder="Search bookings by customer name, email, tour title..."
                value={searchTerm}
                onChange={handleSearchChange}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: "9px 36px 9px 36px",
                  border: `1px solid ${colors.border}`,
                  borderRadius: 8,
                  fontSize: 13,
                  color: colors.text,
                  background: colors.bgSubtle,
                  outline: "none",
                  transition: "border-color .15s ease, background .15s ease",
                }}
                onFocus={(e) => {
                  e.target.style.background = "#fff";
                  e.target.style.borderColor = colors.primary;
                }}
                onBlur={(e) => {
                  e.target.style.background = colors.bgSubtle;
                  e.target.style.borderColor = colors.border;
                }}
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={clearSearch}
                  style={{
                    position: "absolute",
                    right: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: colors.textFaint,
                    display: "flex",
                    padding: 2,
                  }}
                >
                  <XIcon size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Tableau */}
          <div style={{ ...cardStyle, overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 13,
                  textAlign: "left",
                }}
              >
                <thead>
                  <tr
                    style={{
                      background: colors.bgSubtle,
                      borderBottom: `1px solid ${colors.borderLight}`,
                    }}
                  >
                    <th style={{ padding: "12px 14px", fontWeight: 600, color: colors.textMuted }}>Tour</th>
                    <th style={{ padding: "12px 14px", fontWeight: 600, color: colors.textMuted }}>Customer</th>
                    <th style={{ padding: "12px 14px", fontWeight: 600, color: colors.textMuted }}>Email</th>
                    <th style={{ padding: "12px 14px", fontWeight: 600, color: colors.textMuted }}>Date</th>
                    <th style={{ padding: "12px 14px", fontWeight: 600, color: colors.textMuted }}>Status</th>
                    <th style={{ padding: "12px 14px", fontWeight: 600, color: colors.textMuted, textAlign: "center" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBookingTours.length > 0 ? (
                    filteredBookingTours.map((item, index) => (
                      <tr
                        key={item.id}
                        style={{
                          borderBottom: `1px solid ${colors.borderLight}`,
                          transition: "background .15s ease",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = colors.bgSubtle)}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <td style={{ padding: "12px 14px", fontWeight: 500, color: colors.text }}>
                          {item.trip?.title || "N/A"}
                        </td>
                        <td style={{ padding: "12px 14px", color: colors.text }}>
                          {item.firstname} {item.lastname}
                        </td>
                        <td style={{ padding: "12px 14px", color: colors.textMuted }}>{item.email}</td>
                        <td style={{ padding: "12px 14px", color: colors.textMuted }}>
                          {formatDate(item.date_booking)}
                        </td>
                        <td style={{ padding: "12px 14px" }}>
                          <span
                            style={{
                              display: "inline-block",
                              padding: "3px 10px",
                              borderRadius: 12,
                              fontSize: 11,
                              fontWeight: 500,
                              ...getStatusBadgeStyle(item.status),
                            }}
                          >
                            {formatStatus(item.status)}
                          </span>
                        </td>
                        <td style={{ padding: "12px 14px", textAlign: "center" }}>
                          <div style={{ display: "flex", justifyContent: "center", gap: 6 }}>
                            <button
                              type="button"
                              title="View Details"
                              onClick={() => handleEditClick(item.id)}
                              style={{
                                background: "none",
                                border: "none",
                                color: colors.primary,
                                cursor: "pointer",
                                padding: 4,
                                display: "flex",
                                borderRadius: 4,
                              }}
                            >
                              <EyeIcon size={16} />
                            </button>
                            <button
                              type="button"
                              title="Delete"
                              onClick={() => handleDeleteClick(index)}
                              style={{
                                background: "none",
                                border: "none",
                                color: colors.danger,
                                cursor: "pointer",
                                padding: 4,
                                display: "flex",
                                borderRadius: 4,
                              }}
                            >
                              <TrashIcon size={14} color={colors.danger} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} style={{ textAlign: "center", padding: "40px 16px", color: colors.textFaint }}>
                        <div style={{ fontSize: 14, marginBottom: 6 }}>
                          {searchTerm ? "No bookings found matching your search." : "No bookings found."}
                        </div>
                        {searchTerm && (
                          <button
                            type="button"
                            onClick={clearSearch}
                            style={{
                              background: "none",
                              border: "none",
                              color: colors.primary,
                              cursor: "pointer",
                              fontSize: 12,
                              textDecoration: "underline",
                            }}
                          >
                            Clear search
                          </button>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {bookingTours.length > 0 && !searchTerm && (
              <div
                style={{
                  padding: "12px 16px",
                  borderTop: `1px solid ${colors.borderLight}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: "#fff",
                }}
              >
                <span style={{ fontSize: 12, color: colors.textMuted }}>
                  Page {currentPage} of {lastPage}
                </span>
                <div style={{ display: "flex", gap: 4 }}>
                  <button
                    type="button"
                    onClick={() => {
                      if (currentPage > 1) fetchBookingTours(currentPage - 1);
                    }}
                    disabled={currentPage === 1}
                    style={{
                      ...buttonBase,
                      padding: "6px 10px",
                      background: colors.bgSubtle,
                      borderColor: colors.border,
                      color: colors.text,
                      opacity: currentPage === 1 ? 0.4 : 1,
                      cursor: currentPage === 1 ? "not-allowed" : "pointer",
                    }}
                  >
                    <ChevronLeftIcon size={14} />
                  </button>

                  {Array.from({ length: Math.min(5, lastPage) }, (_, i) => {
                    let page: number;
                    if (lastPage <= 5) page = i + 1;
                    else if (currentPage <= 3) page = i + 1;
                    else if (currentPage >= lastPage - 2) page = lastPage - 4 + i;
                    else page = currentPage - 2 + i;

                    const active = currentPage === page;
                    return (
                      <button
                        key={page}
                        type="button"
                        onClick={() => fetchBookingTours(page)}
                        style={{
                          ...buttonBase,
                          padding: "6px 12px",
                          fontSize: 12,
                          background: active ? colors.primary : "#fff",
                          color: active ? "#fff" : colors.text,
                          borderColor: active ? colors.primary : colors.border,
                        }}
                      >
                        {page}
                      </button>
                    );
                  })}

                  <button
                    type="button"
                    onClick={() => {
                      if (currentPage < lastPage) fetchBookingTours(currentPage + 1);
                    }}
                    disabled={currentPage === lastPage}
                    style={{
                      ...buttonBase,
                      padding: "6px 10px",
                      background: colors.bgSubtle,
                      borderColor: colors.border,
                      color: colors.text,
                      opacity: currentPage === lastPage ? 0.4 : 1,
                      cursor: currentPage === lastPage ? "not-allowed" : "pointer",
                    }}
                  >
                    <ChevronRightIcon size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Colonne Droite (20%) - Métriques / Résumé */}
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div
            style={{
              ...cardStyle,
              padding: 16,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0, color: colors.text }}>Summary</h3>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                fontSize: 12,
                color: colors.textMuted,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Showing:</span>
                <span style={{ fontWeight: 600, color: colors.text }}>
                  {filteredBookingTours.length} items
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Total pages:</span>
                <span style={{ fontWeight: 600, color: colors.text }}>{lastPage}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de suppression */}
      {showModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "12px",
              width: "420px",
              maxWidth: "90%",
              overflow: "hidden",
              boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "16px 20px",
                borderBottom: `1px solid ${colors.borderLight}`,
              }}
            >
              <h5 style={{ fontSize: 15, fontWeight: 600, color: colors.text, margin: 0 }}>
                Confirm Deletion
              </h5>
              <button
                type="button"
                onClick={handleCancel}
                style={{
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  fontSize: "1.2rem",
                  color: colors.textFaint,
                }}
              >
                &times;
              </button>
            </div>

            <div style={{ padding: "20px", fontSize: 13, color: colors.textMuted }}>
              Are you sure you want to delete this booking? This action cannot be undone.
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "8px",
                padding: "12px 20px",
                borderTop: `1px solid ${colors.borderLight}`,
                background: colors.bgSubtle,
              }}
            >
              <button
                type="button"
                onClick={handleCancel}
                style={{
                  ...buttonBase,
                  background: "#fff",
                  border: `1px solid ${colors.border}`,
                  color: colors.textMuted,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                style={{
                  ...buttonBase,
                  background: colors.danger,
                  color: "#fff",
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </>
  );
};

export default ListBookingTours;